import { useList } from '@/src/hooks/useList';
import { useTokensStoreForFunds } from '@/src/hooks/useTokensStoreForFunds';
import { FundWithDetailsEntity } from '@/src/lib/MayzSmartDB/Entities/index';
import { FundWithDetailsApi } from '@/src/lib/MayzSmartDB/FrontEnd/index';
import { OptionsGet } from '@/src/lib/SmartDB';
import { strToHex } from '@/src/lib/MayzSmartDB';
import { FUND_STATUS_CLOSED, FUND_STATUS_READY } from '@/src/utils/constants';
import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useWalletStore } from '../store';

export interface IFundListForUsers {
    isLoadingList: boolean;
    list: FundWithDetailsEntity[];
    current: FundWithDetailsEntity | undefined;
    setCurrent: (current?: FundWithDetailsEntity) => void;
    refreshList: (swCalculateTotalPages?: boolean, newCurrentPage?: number) => Promise<void>;
    searchString: string;
    setSearchString: (value: string) => void;
    handlePageChange: (page: number) => void;
    currentPage: number;
    totalPages: number | undefined;
    totalItems: number | undefined;
    typeFilter: string[];
    typeFilterSelected: string;
    setTypeFilterSelected: (value: string) => void;
    isAddingTokens: boolean;
}

export default function useFundListForHome(): IFundListForUsers {
    //--------------------------------------
    const { data: session, status } = useSession();
    const walletStore = useWalletStore();
    //--------------------------------------
    const [isRefreshing, setIsRefreshing] = useState(true);
    useEffect(() => {
        setIsRefreshing(false);
    }, []);
    //--------------------------------------
    const [searchString, setSearchString] = useState<string>('');
    //--------------------------------------
    const typeFilter = ['All', 'MAYZ Official', 'From Community'];
    const [typeFilterSelected, setTypeFilterSelected] = useState<string>(typeFilter[0]);
    //--------------------------------------
    const loadList = async (swCalculateTotalPages?: boolean, currentPage_?: number, itemsPerPage?: number) => {
        //--------
        // console.log(`loadList ${searchString}`);
        //--------
        let paramsBase: Record<string, any> = {};
        const isCoreTeam = walletStore.isCoreTeam(true, session, status);
        if (isCoreTeam) {
            paramsBase = { $or: [{ status: FUND_STATUS_READY }, { status: FUND_STATUS_CLOSED }], _isDeployed: true, showInSite: true };
        } else {
            paramsBase = { $or: [{ status: FUND_STATUS_READY }, { status: FUND_STATUS_CLOSED }], _isDeployed: true, showInSite: true, showInHome: true };
        }
        //--------
        const arrayAnd = [paramsBase];
        //--------
        // console.log(`loadList ${searchString}`);
        if (searchString !== undefined && searchString !== '') {
            const searchString_ = searchString.replace(/[^0-9a-zA-Z\-\_ ]/g, '');
            const params = {
                $or: [
                    { name: `/${searchString_}/i` },
                    { fdFundFT_TN_Hex: `/${strToHex(searchString_)}/i` },
                    { fdFundFT_TN_Hex: `/${strToHex(searchString_.toUpperCase())}/i` },
                    { fdFundFT_TN_Hex: `/${strToHex(searchString_.toLowerCase())}/i` },
                ],
            };
            arrayAnd.push(params);
        }
        //--------
        if (typeFilterSelected === 'MAYZ Official') {
            const params = { isOfficial: true };
            arrayAnd.push(params);
        }
        //--------
        const params: Record<string, any> = arrayAnd.length > 0 ? { $and: arrayAnd } : {};
        //--------
        let totalPages_ = totalPages;
        //--------
        itemsPerPage = itemsPerPage || 1;
        //--------
        if (swCalculateTotalPages === true) {
            const { pages: totalPages_, items: totalItems } = await FundWithDetailsApi.getTotalItemsAndPagesApi_(params, itemsPerPage);
            setTotalPages(totalPages_);
            setTotalItems(totalItems);
        }
        //--------
        let optionsGet: OptionsGet = addPagination(FundWithDetailsEntity.optionsGetForFundList_ForPublic, totalPages_!, currentPage_!, itemsPerPage);
        //--------
        const funds: FundWithDetailsEntity[] = await FundWithDetailsApi.getByParamsApi_(params, optionsGet);
        //--------------------------------------
        return funds;
    };
    //--------------------------------------
    function checkDependencies() {
        let doLoadList = false;
        if (searchString !== undefined) {
            doLoadList = true;
        }
        return doLoadList;
    }
    //--------------------------------------
    const {
        isLoadingList,
        isLoadedList,
        list,
        current,
        refreshList,
        setCurrent,
        handlePageChange,
        currentPage,
        setTotalPages,
        totalPages,
        totalItems,
        setTotalItems,
        addPagination,
    } = useList<FundWithDetailsEntity>({
        nameList: FundWithDetailsEntity.className(),
        loadList,
        checkDependencies,
        dependencies: [searchString, typeFilterSelected],
    });
    //--------------------------------------
    const { isTokensStoreReady, tokensFromStore, isAddingTokens } = useTokensStoreForFunds({
        mode: 'FundList',
        name: 'FundListForUsers',
        isLoadedList,
        funds: list,
        followUp: undefined,
        keepAlive: undefined,
        addTokenFT: true,
        addSubTokensFT: false,
        swAddPrice: true,
        swAddMetadata: true,
        initPriceUpdater: false,
    });
    //--------------------------------------
    return {
        isLoadingList,
        list,
        current,
        refreshList,
        setCurrent,
        searchString,
        setSearchString,
        handlePageChange,
        currentPage,
        totalPages,
        totalItems,
        typeFilter,
        typeFilterSelected,
        setTypeFilterSelected,
        isAddingTokens,
    };
    //--------------------------------------
}
